import { IDate } from '../date';
import { IOrganizationRecordHeader, isIOrganizationRecordHeader, organizationHeaderProperties } from './record';
import { IInventory, inventoryProperties, isIInventory } from './inventory';
import { IForecast, forecastProperties, isIForecast } from './forecast';
import { IOrder, isIOrder, orderProperties } from './hasRecommendOrders';
import { Arrivals, arrivalsProperties } from './arrival';
import { SalesResults, salesResultsProperties } from './salesResults';
import { UnionToArray } from '@/appLogic/utilityTypes/unionToArray';
import { BusinessFactory } from '../factories/BusinessFactory';
import { UserAccount } from './users';
import { UnmanagedBusinessPlaceholderField } from '../constants/unmanagedBusinessAutoRegister';

/**
 * IBusinessBaseで、型が実装されていない場合、このクラスになる
 * 中身はunknownなので基本上書き推奨
 */
type defaultBusinessBasePropertyType = unknown;

/**
 * IBusinessBaseのプロパティを型として定義する
 */
type mappedBusinessBaseProperties = { [K in RequiredBusinessBasePropertyType]?: defaultBusinessBasePropertyType };

/**
 * IBusinessBaseのプロパティを型として定義する
 */
type RequiredBusinessBasePropertyType = keyof IBusinessBase;

/**
 * appLogic/interfaces/businessBase.ts に定義されているものを移植、不必要なものを取り除いたもの
 * @property businessId - 取引ID
 * @property salesDestinationName - 販売先
 * @property salesDestinationNumber - 販売先CD
 * @property MOQ - 最小発注単位
 * @property orderPackage - 発注単位
 * @property note - 備考
 * @property leadTime - リードタイム
 * @property leadTimeUnit - リードタイムの単位
 * @property inTransitDays - 国内輸送日数
 * @property group - 共通化したbusinessId群
 * @property aggregateGroup - エクセルの集計行にある取引に関連するbusinessId群
 * @property virtualChild - 共通化、展開UIに用いるためのもの
 * @property isTrashed - ゴミ箱に入っているかどうか
 * @property createAt - このbusinessの登録日時
 * @property customerName - 売上先
 * @property customerNumber - 売上先CD
 * @property businessName -　商品名
 * @property unit - 単位
 * @property deliveryDestinationName - 納入先
 * @property businessForecastState - 内示のステータス(確定発注/仕入先内示 への追加が済んでいるかどうか)
 * @property executionDate - エクセル取り込みの実施日（エクセル取り込みの時に付与される）
 * @property businessMemo - エクセル取り込み時に任意選択で取得する情報
 * @property forecastForReceiptId - 受領用内示ID
 * @property aggregateBusinessResponsibleUsers - 仕入担当者ID
 * @property baseBusinessResponsibleUsers - 販売担当者ID
 */
type IBusinessBase = {
  businessId: string;
  salesDestinationName: string | undefined;
  salesDestinationNumber: string | undefined;
  MOQ: number | undefined;
  orderPackage: number | undefined;
  note: string | undefined;
  leadTime: number | undefined;
  leadTimeUnit: string | undefined;
  inTransitDays: number | undefined;
  group: string[] | undefined;
  aggregateGroup: string[] | undefined;
  virtualChild: boolean | undefined;
  isTrashed: boolean | undefined;
  createAt: IDate | undefined;
  customerName: string | undefined;
  customerNumber: string | undefined;
  businessName: string | undefined;
  unit: string | undefined;
  deliveryDestinationName: string | undefined;
  executionDate: Date | undefined;
  businessMemo: IBusinessMemo | undefined; // 取引に関するメモ(現状、顧客台帳IDをここに入れている)
  forecastForReceiptId: string | undefined;
  forecastForReceiptName: string | undefined;
  aggregateBusinessResponsibleUsers: UserAccount[] | undefined;
  baseBusinessResponsibleUsers: UserAccount[] | undefined;
  forecastForReceiptResponsibleUsers: UserAccount[] | undefined;
  manufacturer: string | undefined;
  endUser: string | undefined;
  lastCheckedAt: Date | undefined;
  lastCheckedUser: UserAccount | undefined;
  purchaseUnitPrice: number | undefined;
  salesUnitPrice: number | undefined;
  placeholderFields: UnmanagedBusinessPlaceholderField[] | undefined;
  userUniqueNumber: string | undefined;
  businessFreeColumnValues: BusinessFreeColumnValue[] | undefined;
};

/**
 * 取引自由欄の値
 */
export type BusinessFreeColumnValue = {
  columnNo: number;
  headerName: string;
  value: string;
  isMulti: boolean;
  /**
   * ユーザーが設定ページで保存済みかどうか。未保存（自動デフォルト）の場合は false。
   */
  isConfigured: boolean;
  /**
   * 自由欄の対象（集計取引/関連取引）。columnNo は対象ごとに 1 から採番されるため、
   * 集計・関連を1つのリストで扱う場面（未管理登録など）で対象を区別するのに使う。
   */
  target: 'aggregate' | 'base' | undefined;
};

/**
 * aggregator.tsで利用する
 *
 * TODO: ここに定義すると、IBusinessBaseにも型:unknownとして追加されるので、適切な型に変更する
 */
export const businessBaseProperties = [
  'businessId',
  'salesDestinationName',
  'MOQ',
  'note',
  'leadTime',
  'inTransitDays',
  'group',
  'aggregateGroup',
  'virtualChild',
  'isTrashed',
  'createAt',
  'customerName',
  'businessName',
  'unit',
  'deliveryDestinationName',
  'executionDate',
  'businessMemo',
  'forecastForReceiptId',
  'forecastForReceiptName',
  'aggregateBusinessResponsibleUsers',
  'baseBusinessResponsibleUsers',
  'purchaseUnitPrice',
  'salesUnitPrice',
  'forecastForReceiptResponsibleUsers',
  'businessFreeColumnValues',
] as const; // TODO: ts versionを4.7以上にしたら as const satisfies UnionToArray<(keyof IBusinessBase)>;

// businessBasePropertiesをSet型に変換したオブジェクト
// プロパティチェック用 businessBasePropertySetをどこかで呼んで、Set自体を変更することは原則禁止です
// Set.has()としてはbusinessPropertiesによって判定したいが、入力としてはstringとして受け入れたいので明示的に型引数にstringを代入している
const businessBasePropertySet = new Set<string>(businessBaseProperties);

/**
 * IBusinessのプロパティを型として定義する
 */
type BusinessPropertyType = keyof IBusiness;

/**
 * LEANPATHで定義されている取引
 */
type IBusiness = IOrganizationRecordHeader &
  IBusinessBase &
  RequiredBusinessProperty &
  IForecast &
  IInventory &
  IOrder &
  Partial<Arrivals> &
  Partial<SalesResults>;

/**
 *
 */
type RequiredBusinessProperty = {
  businessId: string; //取引ID
  warehouse: string; // 倉庫
  warehouseNumber: string; // 倉庫CD
  package: string; // 容量
  businessNumber: string; // 商品CD
  supplierName: string; // 仕入先
  supplierNumber: string; // 仕入先CD
};

/**
 * 管理表のメモ欄に入れる情報
 * 列名(key)と内容をセットで持つ
 */
type IBusinessMemo = { [key: string]: string };

const requireBusinessProperties = [
  'businessId',
  'warehouse',
  'warehouseNumber',
  'package',
  'businessNumber',
  'supplierName',
  'supplierNumber',
] as const;

const businessProperties = [
  ...organizationHeaderProperties,
  ...businessBaseProperties,
  ...requireBusinessProperties,
  ...forecastProperties,
  ...inventoryProperties,
  ...orderProperties,
  ...arrivalsProperties,
  ...salesResultsProperties,
] as const;

// businessPropertiesをSet型に変換したオブジェクト
// プロパティチェック用 businessPropertySetをどこかで呼んで、Set自体を変更することは原則禁止です
// Set.has()としてはbusinessPropertiesによって判定したいが、入力としてはstringとして受け入れたいので明示的に型引数にstringを代入している
const businessPropertySet = new Set<string>(businessProperties);

/**
 *
 * @param value - 任意の値
 */
function isRequiredBusinessProperty(value: object): value is RequiredBusinessProperty {
  return (
    Object.prototype.hasOwnProperty.call(value, 'businessId') &&
    Object.prototype.hasOwnProperty.call(value, 'warehouse') &&
    Object.prototype.hasOwnProperty.call(value, 'warehouseNumber') &&
    Object.prototype.hasOwnProperty.call(value, 'package') &&
    Object.prototype.hasOwnProperty.call(value, 'businessNumber') &&
    Object.prototype.hasOwnProperty.call(value, 'supplierName') &&
    Object.prototype.hasOwnProperty.call(value, 'supplierNumber')
  );
}

/**
 *
 * @param value - 任意の値
 */
function isIBusiness(value: object): value is IBusiness {
  return (
    isIOrganizationRecordHeader(value) && isRequiredBusinessProperty(value) && isIForecast(value) && isIInventory(value) && isIOrder(value)
  );
}

/**
 *
 * @param value - 任意の値
 */
function assertsIBusiness(value: object): asserts value is IBusiness {
  if (!isIBusiness(value)) {
    throw Error('IBusinessに変換できません');
  }
}

/**
 * IBusinessBaseに定義されたプロパティかを確認する
 * ※厳密には、businessBasePropertiesという配列に入っているものとの比較を行なっているので、
 * IBusinessBaseとbusinessBasePropertiesは整合性を合わせる必要がある
 * @param value - 比較したいプロパティ情報
 */
function isIBusinessBaseProperty(value: RequiredBusinessBasePropertyType): boolean {
  return businessBasePropertySet.has(value);
}

/**
 * IBusinessとして定義されたプロパティかを確認する
 * ※businessPropertiesという配列に入っているものとの比較を行なっているので、
 * IBusinessとbusinessPropertiesは整合性を合わせる必要がある
 * TODO: IBusinessに関わるプロパティ配列の中身がIBusinessと異なるため、Setで書く場合はプロパティ配列をどうにかする必要がある
 * @param value - 比較したいプロパティ情報
 */
function isIBusinessProperty(value: BusinessPropertyType): boolean {
  return businessPropertySet.has(value);
}

/**
 * IBusinessを拡張したオブジェクト(IBusinessの部分群型) -> IBusinessにアップキャストする際に不要なプロパティ(IBusinessに追加したプロパティ)を削ぎ落とす
 * 引数にreadonlyをつけたのは、{propertyArray}を配列内で書き換えしない事が保証されている+readonly配列も受けられる様にするためです(後半が主目的)
 * {propertyArray}に渡す配列はreadonlyでなくとも関数に渡すことはできます
 * @param businessObjectArray - IBusinessを拡張したオブジェクトの配列
 * @param excludePropertyArray - 削ぎ落としたい(IBusinessにextendsしたプロパティ群)プロパティの一覧をまとめた配列
 * @returns  アップキャスト後のIBusinessを満たす配列
 * NOTE: もともと(excludePropertyArray: keyof T extends keyof IBusiness | infer U ? Readonly<UnionToArray<U>> : [])としていたが、タプルの順序が保証されないので変更している。ts4.9以上なら安定するかも？
)
 */
export function extendedBusinessToIBusiness<T extends IBusiness, K extends readonly (keyof T extends keyof IBusiness ? never : keyof T)[]>(
  businessObjectArray: T[],
  excludePropertyArray: K
): IBusiness[] {
  for (const businessObject of businessObjectArray) {
    for (const key of excludePropertyArray) {
      delete businessObject[key as any]; // 特定のプロパティを削除する
    }
  }
  return businessObjectArray;
}

/**
 * IBusinessのキーの型keyOfIBusinessを取得
 */
type keyOfIBusiness = keyof IBusiness;

/**
 * keyOfIBusinessがIBusiness型のキーに存在するかを確認
 * @param key - 確認するキー
 */
const isKeyOfIBusiness = (key: string): key is keyOfIBusiness => {
  const dummyObject: IBusiness = BusinessFactory.create();
  return key in dummyObject;
};

/**
 * dummyIBusiness（required)を取得して、そのキーに対応する型を取得
 * @param key - 情報を取得するキー
 */
const getKeyType = <K extends keyof IBusiness>(key: K): IBusiness[K] => {
  const dummyObject: IBusiness = BusinessFactory.create();
  return dummyObject[key];
};

export type { IBusinessBase, IBusiness, IBusinessMemo, RequiredBusinessProperty };
export { isIBusiness, assertsIBusiness, isIBusinessBaseProperty, isIBusinessProperty };
export { isKeyOfIBusiness, getKeyType }; // 坂井作成分、↑のやつといずれ統一したい
